import { PointerEvent as ReactPointerEvent, useCallback, useMemo, useRef, useState } from 'react'
import { useLatest } from 'react-use'
import { useSceneContextMenuAction } from '@editor/hooks'
import type { DropKind, Results2ListItem } from '../types'

const canAddChildInGroup = (
  items: Results2ListItem[],
  parentId: string,
  childId: string,
): boolean => {
  if (parentId === childId) return false
  const parent = items.find(i => i.id === parentId)
  const child = items.find(i => i.id === childId)
  if (!parent || !child) return false
  if (child.parent === parentId) return false
  if (items.some(i => i.parent === childId)) return false
  return true
}

const canDropOntoTarget = (items: Results2ListItem[], targetId: string): boolean => {
  const target = items.find(i => i.id === targetId)
  return Boolean(target && !target.parent)
}

type UseGroupingDragAndDropProps = {
  disableForm: boolean
  groupingEnabled: boolean
  isTree: boolean
  items: Results2ListItem[]
  selectedId?: string
  selectedIds?: string[]
  selectedIdSet: Set<string>
  childCountByParent: Record<string, number>
  /** Apply a grouping mutation to the form items (marks the form dirty → triggers autosave). */
  setItems: (updater: (current: Results2ListItem[]) => Results2ListItem[]) => void
  /** Single-select a row; returns false when the selection change was vetoed. */
  selectRow: (id: string) => boolean
}

type UseGroupingDragAndDropResult = {
  dragIds: string[]
  dropTarget: { id: string; kind: DropKind } | null
  startPointerDrag: (id: string) => (e: ReactPointerEvent<HTMLElement>) => void
  canShowAddToGroupButton: (itemId: string) => boolean
  addToSelectedGroup: (childId: string) => void
}

/**
 * Drag & drop grouping of list rows (reorder, group under a parent) plus the matching
 * "Zur Gruppe hinzufügen" actions on rows and in the 3D scene context menu.
 */
export const useGroupingDragAndDrop = ({
  disableForm,
  groupingEnabled,
  isTree,
  items,
  selectedId,
  selectedIds,
  selectedIdSet,
  childCountByParent,
  setItems,
  selectRow,
}: UseGroupingDragAndDropProps): UseGroupingDragAndDropResult => {
  const [dragIds, setDragIds] = useState<string[]>([])
  const [dropTarget, setDropTarget] = useState<{ id: string; kind: DropKind } | null>(null)
  // Pointer handlers attach to window for the duration of a drag — they read the live values
  // through refs because the closures outlive the render that created them.
  const dragIdsRef = useRef<string[]>([])
  const dropTargetRef = useRef<{ id: string; kind: DropKind } | null>(null)

  const endDrag = useCallback(() => {
    dragIdsRef.current = []
    dropTargetRef.current = null
    setDragIds([])
    setDropTarget(null)
  }, [])

  const performDrop = useCallback(
    ({
      activeDragIds,
      targetId,
      kind,
    }: {
      activeDragIds: string[]
      targetId: string
      kind: DropKind
    }) => {
      setItems(current => {
        const activeIds = Array.from(new Set(activeDragIds)).filter(id => id !== targetId)
        const activeSet = new Set(activeIds)
        const tgt = current.find(i => i.id === targetId)
        const moving = current.filter(i => activeSet.has(i.id))
        if (!tgt || moving.length === 0 || activeSet.has(tgt.id)) return current
        if (kind === 'onto' && !canDropOntoTarget(current, targetId)) return current
        if (kind === 'onto' && tgt.parent && activeSet.has(tgt.parent)) return current

        const without = current.filter(i => !activeSet.has(i.id))
        const nextParent = kind === 'onto' ? tgt.id : tgt.parent
        const movedItems = moving.map(item => ({ ...item, parent: nextParent, ok: false }))

        const idx = without.findIndex(i => i.id === tgt.id)
        if (idx === -1) return current

        if (kind === 'onto') {
          // Allow creating a new group by dropping onto a non-parent element:
          // promote the target to a top-level parent, then attach selected items underneath it.
          const next = [...without]
          next[idx] = { ...next[idx], parent: undefined, ok: true }
          next.splice(idx + 1, 0, ...movedItems)
          return next
        }

        without.splice(kind === 'before' ? idx : idx + 1, 0, ...movedItems)
        return without
      })
    },
    [setItems],
  )

  const groupParentId = useMemo(() => {
    if (selectedIds?.length === 1) return selectedIds[0]
    if (selectedId) return selectedId
    return null
  }, [selectedId, selectedIds])

  const itemsRef = useLatest(items)
  const groupParentIdRef = useLatest(groupParentId)
  const groupingEnabledRef = useLatest(groupingEnabled)
  const isTreeRef = useLatest(isTree)
  const performDropRef = useLatest(performDrop)

  useSceneContextMenuAction({
    label: 'Zur Gruppe hinzufügen',
    group: 'results-grouping',
    available: clickedElement => {
      const parentId = groupParentIdRef.current
      if (!groupingEnabledRef.current || !isTreeRef.current || !parentId || !clickedElement) {
        return false
      }
      return canAddChildInGroup(itemsRef.current, parentId, clickedElement)
    },
    action: clickedElement => {
      const parentId = groupParentIdRef.current
      if (!parentId || !clickedElement) return
      if (!canAddChildInGroup(itemsRef.current, parentId, clickedElement)) return
      performDropRef.current({
        activeDragIds: [clickedElement],
        targetId: parentId,
        kind: 'onto',
      })
    },
  })

  const canShowAddToGroupButton = useCallback(
    (itemId: string) => {
      const parentId = groupParentId
      if (!groupingEnabled || !isTree || !parentId) return false
      const parentItem = items.find(i => i.id === parentId)
      if (parentItem?.parent) return false
      if (selectedIdSet.has(itemId)) return false
      const item = items.find(i => i.id === itemId)
      if (!item || item.parent) return false
      if ((childCountByParent[itemId] ?? 0) > 0) return false
      return canAddChildInGroup(items, parentId, itemId)
    },
    [childCountByParent, groupParentId, groupingEnabled, isTree, items, selectedIdSet],
  )

  const addToSelectedGroup = useCallback(
    (childId: string) => {
      const parentId = groupParentId
      if (!parentId || !canAddChildInGroup(items, parentId, childId)) return
      performDrop({
        activeDragIds: [childId],
        targetId: parentId,
        kind: 'onto',
      })
    },
    [groupParentId, items, performDrop],
  )

  const startPointerDrag = useCallback(
    (id: string) => (e: ReactPointerEvent<HTMLElement>) => {
      if (disableForm || e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()

      // Dragging a selected row drags the whole selection (parents excluded — groups don't nest).
      const selectedDragIds =
        selectedIdSet.has(id) && (selectedIds?.length ?? 0) > 1
          ? selectedIds?.filter(
              (selected: string) => (childCountByParent[selected] ?? 0) === 0,
            ) ?? [id]
          : [id]
      const nextDragIds = selectedDragIds.length > 0 ? selectedDragIds : [id]

      if (!selectedIdSet.has(id) && !selectRow(id)) return

      dragIdsRef.current = nextDragIds
      dropTargetRef.current = null
      setDragIds(nextDragIds)
      setDropTarget(null)

      const onMove = (ev: PointerEvent) => {
        const activeDragIds = dragIdsRef.current
        if (activeDragIds.length === 0) return
        const activeSet = new Set(activeDragIds)

        const el = document.elementFromPoint(ev.clientX, ev.clientY)
        const rowEl = el && 'closest' in el ? (el as Element).closest('[data-row-id]') : null
        const targetId = rowEl ? rowEl.getAttribute('data-row-id') : null

        if (!targetId || activeSet.has(targetId)) {
          dropTargetRef.current = null
          setDropTarget(null)
          return
        }

        const rect = (rowEl as HTMLElement).getBoundingClientRect()
        const y = ev.clientY - rect.top
        const h = rect.height || 1

        let kind: DropKind
        if (y > h * 0.3 && y < h * 0.7) kind = 'onto'
        else if (y < h / 2) kind = 'before'
        else kind = 'after'

        if (kind === 'onto' && !canDropOntoTarget(itemsRef.current, targetId)) {
          kind = y < h / 2 ? 'before' : 'after'
        }

        const next = { id: targetId, kind }
        dropTargetRef.current = next
        setDropTarget(next)
      }

      const onUp = () => {
        const activeDragIds = dragIdsRef.current
        const activeDrop = dropTargetRef.current
        if (activeDragIds.length > 0 && activeDrop) {
          performDrop({
            activeDragIds,
            targetId: activeDrop.id,
            kind: activeDrop.kind,
          })
        }
        window.removeEventListener('pointermove', onMove, true)
        window.removeEventListener('pointerup', onUp, true)
        window.removeEventListener('pointercancel', onUp, true)
        endDrag()
      }

      window.addEventListener('pointermove', onMove, true)
      window.addEventListener('pointerup', onUp, true)
      window.addEventListener('pointercancel', onUp, true)
    },
    [
      childCountByParent,
      disableForm,
      endDrag,
      itemsRef,
      performDrop,
      selectRow,
      selectedIds,
      selectedIdSet,
    ],
  )

  return { dragIds, dropTarget, startPointerDrag, canShowAddToGroupButton, addToSelectedGroup }
}
